import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Clock, MapPin, Monitor, Sparkles, Users } from "lucide-react";
import { formatPrice } from "@/lib/programs";

/**
 * Accent theme: Tailwind class strings (kept as full literals so Tailwind can see them).
 * Industry pages pass their own theme from src/data/industries.js (blue or orange tints only).
 */
export const DEFAULT_PROGRAM_THEME = {
  accentText: "text-brand-600",
  accentBg: "bg-brand-500",
  accentSoft: "bg-brand-100 text-ink",
  accentHoverBg: "group-hover:bg-brand-500 group-hover:text-white",
  accentRing: "hover:border-brand-500",
};

function ModeIcon({ mode }) {
  const isVirtual = /virtual|online/i.test(mode || "");
  const Icon = isVirtual ? Monitor : MapPin;
  return <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />;
}

function MetaItem({ icon: Icon, children }) {
  return (
    <li className="inline-flex items-center gap-1.5">
      <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </li>
  );
}

/**
 * One program. Expects a normalized Program (see src/lib/programs.js).
 *
 * SEO/a11y notes:
 * - Rendered as <article> with an <h3>; the section that contains it must provide the <h2>.
 * - Exactly one link per card (the title link is stretched over the card).
 * - The image has alt text and explicit width/height to avoid layout shift.
 * - Light theme only. `featured` shows an orange badge; it does not change the card layout.
 */
export function ProgramCard({ program, theme = DEFAULT_PROGRAM_THEME, testId }) {
  const { title, summary, image, imageAlt, mode, duration, startDate, venue, price, href, external, featured, kind, ages } = program;
  const priceLabel = formatPrice(price);

  const linkClasses =
    "after:absolute after:inset-0 after:content-[''] focus:outline-none focus-visible:after:ring-4 focus-visible:after:ring-sun-500 focus-visible:after:rounded-3xl";
  const linkProps = external ? { href, target: "_blank", rel: "noopener noreferrer" } : {};
  const LinkTag = external ? "a" : Link;
  const linkTarget = external ? {} : { to: href };

  return (
    <article
      data-testid={testId}
      className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border border-brand-200 bg-white text-ink transition-all duration-300 hover:shadow-soft ${theme.accentRing}`}
    >
      <div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-brand-100">
        <img
          src={image}
          alt={imageAlt}
          width={800}
          height={500}
          loading="lazy"
          decoding="async"
          onError={(event) => {
            // A broken remote image should leave the tinted placeholder, not stray alt text.
            event.currentTarget.style.visibility = "hidden";
          }}
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />

        <div className="absolute left-3 top-3 flex flex-wrap items-center gap-2 sm:left-4 sm:top-4">
          {mode ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-ink">
              <ModeIcon mode={mode} />
              {mode}
            </span>
          ) : null}
          {featured ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-500 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              Featured
            </span>
          ) : null}
        </div>

        {duration ? (
          <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-ink sm:bottom-4 sm:left-4">
            <Clock className="w-3.5 h-3.5" aria-hidden="true" />
            {duration}
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col p-5 sm:p-6">
        {kind ? (
          <p className={`mb-2 text-[11px] font-bold uppercase tracking-wider ${theme.accentText}`}>{kind}</p>
        ) : null}
        <h3 className="font-heading text-xl font-bold leading-snug">
          <LinkTag {...linkTarget} {...linkProps} className={linkClasses}>
            {title}
            {external ? <span className="sr-only"> (opens in a new tab)</span> : null}
          </LinkTag>
        </h3>

        {summary ? <p className="mt-3 text-sm leading-relaxed text-ink/70 line-clamp-3 sm:text-base">{summary}</p> : null}

        {startDate || venue || ages ? (
          <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1.5 text-xs font-medium text-ink/70">
            {startDate ? <MetaItem icon={CalendarDays}>{startDate}</MetaItem> : null}
            {venue ? <MetaItem icon={MapPin}>{venue}</MetaItem> : null}
            {ages ? <MetaItem icon={Users}>Ages {ages}</MetaItem> : null}
          </ul>
        ) : null}

        <div className="mt-auto flex items-center justify-between gap-4 border-t-2 border-brand-100 pt-5">
          <div>
            {priceLabel ? (
              <>
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-ink/60">Programme fee</span>
                <span className="text-xl font-bold">{priceLabel}</span>
              </>
            ) : (
              <span className="text-sm font-semibold text-ink/70">Enquire for fees</span>
            )}
          </div>

          <span
            aria-hidden="true"
            className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition-all duration-300 ${theme.accentSoft} ${theme.accentHoverBg}`}
          >
            <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-0.5 " />
          </span>
        </div>
      </div>
    </article>
  );
}
